// ThemeList.tsx
import { Button, CheckBox, DataGrid, DropDownButton } from "devextreme-react";
import { Column, DataGridRef, Editing, Export, FilterBuilderPopup, FilterPanel, ColumnChooser,FilterRow, HeaderFilter, Item, LoadPanel, RowDragging, Scrolling, SearchPanel, Selection, Sorting, Toolbar } from "devextreme-react/cjs/data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteSettingRequest, getSettingListRequest, setDefaultSettingRequest } from "../../../api/requests";
import notify from 'devextreme/ui/notify';
import SettingsPopup, { SavedSetting } from "../../../components/library/page-setting/SettingsPopup";
import { PageSettings } from "../../../helpers/pageSettings";
import { handleSettingsSaveAction } from "../../../helpers/settingsActions";
import { applySettingsToGrid, fetchDefaultSettings, getCurrentGridColumns } from "../../../helpers/settingsHelper";
import CustomSettingsToolbarItem from "../../../helpers/CustomSettingsToolbarItem";
import ThemePopup from "./popup/popup";
import { appInfo } from '../../../app-info';

// Boş tema bilgilerini içeren sabit (ThemeSettingsModel tipinde)
export type ThemeSettingsModel = {
    settingRef?: number;
    settingName?: string;
    comment?: string;
    isDefault?: string | number;
    settingJson?: string;
    settingStatus?: number;

    grid_row_padding?: number;
    grid_row_font?: number;
    grid_row_bold?: boolean;
    grid_row_color?: string;
    grid_row_fontfamily?: string;
    grid_header_padding?: number;
    grid_header_font?: number;
    grid_header_bold?: boolean;
    grid_header_color?: string;
    grid_header_fontfamily?: string;
    grid_row_alterneColor?: string;

    menu_fontfamily?: string;
    menu_fontsize?: number;
};
export const emptyThemeSettings: ThemeSettingsModel = {
    grid_row_padding: 3,
    grid_row_font: 12,
    grid_row_bold: false,
    grid_row_color: "#000000",
    grid_row_fontfamily: "Arial, sans-serif",
    grid_header_padding: 0,
    grid_header_font: 12,
    grid_header_bold: true,
    grid_header_color: "#000000",
    grid_header_fontfamily: "Arial, sans-serif"
};

export const ThemeList = () => {
    const gridRef = useRef<DataGridRef>(null);
    const { t } = useTranslation();
    const [theme, setThemes] = useState([]);
    const [selectedTheme, setSelectedTheme] = useState<ThemeSettingsModel | null>(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [settingStatus, setSettingStatus] = useState<number>(1); // Varsayılan: Active
    const [showFilterRow, setShowFilterRow] = useState(false);

    // const getList = () => {
    //     getSettingListRequest(settingStatus).then((resp) => {
    //         if (resp.resCode === 200 && resp.dataSet && resp.dataSet.length > 0) {
    //             setThemes(resp.dataSet);
    //         } else {
    //             setThemes([]);
    //         }
    //     });
    // }, [settingStatus];

    const getList = useCallback(() => {
        getSettingListRequest(settingStatus).then((res) => {
            setThemes(res.dataSet || []);
            // setSettingStatus(res.dataSet[0].settingStatus);

            gridRef.current?.instance().endCustomLoading();

        });
    }, [settingStatus]);

    useEffect(() => {
        getList();
    }, [getList]);

    // Hücre tıklanması durumunda; "settingName" kolonuna tıklanırsa
    // const onCellClick = useCallback(({ data }) => {
    //     if (!data) return; // Guard clause to exit if data is undefined
    //     const selectedRowData = JSON.parse(JSON.stringify(data));
    //     setSelectedTheme(selectedRowData);
    //     setPopupVisible(true);
    //   }, []);
    const onCellClick = useCallback(({ data, column }) => {
        // Eğer tıklanan sütun "isDefault" ise popup açma
        if (column?.dataField === "isDefault") {
            return;
        }
        if (!data) return;
        const selectedRowData = JSON.parse(JSON.stringify(data));
        setSelectedTheme(selectedRowData);
        setPopupVisible(true);
    }, []);


  const rowCountRef = useRef<HTMLSpanElement>(null);
  const updateRowCountDisplay = useCallback((count: number) => {
    if (rowCountRef.current) {
      rowCountRef.current.textContent = `${count} ${t("records")}`;
    }
  }, []);
    const handleStatusChange = (e: any) => {
        if (e?.item?.value !== undefined) {
            setSettingStatus(Number(e.item.value)); // Seçilen değeri kullan
        } else {
            console.error("item veya value tanımsız!", e);
        }
    };



    // Satır silme işlemi: API çağrısı yaparak silme işlemi gerçekleştirilir.
    const onRowRemoving = async (e) => {
        const settingRef = e.data.settingRef;
        return deleteSettingRequest(settingRef)
            .then((response) => {
                if (response.resCode == 200) {
                    notify(t("successfull"), "success", 1000);
                } else {
                    notify(response.resMessage, "error", 2500);
                    getList();
                    throw new Error(response.resMessage);

                }
            })
            .catch((error) => {
                console.error('Error deleting product:', error);
            });
    };
    const onRowClick = useCallback((e) => {
        // Eğer tıklanan eleman bir checkbox ise, popup açılmasını engelle
        if (e.event?.target?.closest('.dx-checkbox')) {
            return;
        }

        if (!e.data) return;
        const selectedRowData = JSON.parse(JSON.stringify(e.data));
        setSelectedTheme(selectedRowData);
        setPopupVisible(true);
    }, []);

    // Plus butonuna tıklanıldığında popup'ı boş şekilde açmak için.
    const openEmptyPopup = () => {
        setSelectedTheme(emptyThemeSettings); // Yeni tema eklemek için boş tema
        setPopupVisible(true);
    };

    // Ayar yönetimi değişkenleri
    const [settingsPopupVisible, setSettingsPopupVisible] = useState<boolean>(false);
    const [savedSettings, setSavedSettings] = useState<SavedSetting[]>([]);
    const [settingsDropdownOpened, setSettingsDropdownOpened] = useState(false);
    const currentPageSettings = PageSettings.themeSettings;
    const settingPage = currentPageSettings.path;
    const settingObject = currentPageSettings.settingObject;
    const handleSettingsSave = async ({ settingName, settingRef, settingType, isDefault }: { settingName: string; settingRef: number; settingType: number; isDefault: number }) => {
        await handleSettingsSaveAction({
            gridInstance: gridRef.current?.instance(),
            settingPage,
            settingObject,
            settingName,
            settingRef,
            settingType,
            isDefault,
            setSavedSettings,
            setSettingsPopupVisible,
        });
    };
    const handleSettingsItemClick = (e: any) => {
        if (e.itemData.value === "modify") {
            // Ayarları değiştirmek için popup açılması
            setSettingsPopupVisible(true);
        } else {
            const setting = e.itemData.value as SavedSetting;
            applySettingsToGrid(gridRef.current?.instance(), setting.settingJson);
        }
    };
    useEffect(() => {
        (async () => {
            const defaultCols = await fetchDefaultSettings(settingPage, settingObject);
            if (gridRef.current?.instance() && defaultCols.length > 0) {
                applySettingsToGrid(gridRef.current.instance(), JSON.stringify(defaultCols));
            }
        })();
    }, []);

    return (
        <div className="view crm-contact-list">
            <div className="view-wrapper view-wrapper-contact-list">
                <DataGrid
                    className="custom-grid compact-grid"
                    dataSource={theme}
                    keyExpr="settingRef"
                    onRowClick={onRowClick}
                    onContentReady={(e) => {
                        const count = e.component.totalCount();
                        updateRowCountDisplay(count);
                        // State sadece ilk yükleme için
                        if (rowCount === 0) {
                            setRowCount(count);
                        }
                    }}
                    onRowRemoving={onRowRemoving}  // Silme işlemi için event handler ekleniyor.
                    noDataText={t("noData")}
                    allowColumnReordering
                    allowColumnResizing
                    columnResizingMode="widget"
                    rowAlternationEnabled
                    showBorders
                    ref={gridRef}
                    editing={{
                        mode: 'row',
                        allowUpdating: false,
                        allowDeleting: true,
                        allowAdding: false,
                    }}
                >
                    <FilterRow visible={showFilterRow} applyFilter={"auto"} />
                    <ColumnChooser enabled />
                     <Export enabled allowExportSelectedData formats={['xlsx', 'pdf']} />
 
                    <SearchPanel visible placeholder={t("search")} />
                    <HeaderFilter visible />
                    <Sorting mode="multiple" />
                    <Scrolling mode="virtual" />
                    <Selection selectAllMode="allPages" showCheckBoxesMode="always" mode="multiple" />
                    <FilterPanel visible={true} />
                    <FilterBuilderPopup width="50%" height="50%" title={t("filterBuilder")} />
                    
                    <Toolbar>

                        <Item name="searchPanel" location="before" />
                        <Item location='before' locateInMenu="auto" >
                            <Button
                                icon='endswith'
                                hint={t("detailedSearch")}
                                stylingMode={showFilterRow ? "contained" : "text"}
                                onClick={() => { setShowFilterRow(!showFilterRow) }}
                            />
                        </Item>
                        <Item name='columnChooserButton' location='before' locateInMenu="auto" />
                        <Item location="before" locateInMenu="auto">
                            <CustomSettingsToolbarItem
                                gridRef={gridRef}
                                settingPage={settingPage}
                                settingObject={settingObject}
                                t={t}
                                savedSettings={savedSettings}
                                setSavedSettings={setSavedSettings}
                                settingsDropdownOpened={settingsDropdownOpened}
                                setSettingsDropdownOpened={setSettingsDropdownOpened}
                                handleSettingsItemClick={handleSettingsItemClick}
                            />
                        </Item>
                        <Item name="exportButton" location="before" />
                        <Item location="before" locateInMenu="auto" showText="inMenu">
                            <Button icon="refresh" hint={t("refresh")} stylingMode="text" onClick={getList} />
                        </Item>
                        
                        <Item location="before">
                            <div className="hidden md:block separator" />
                        </Item>

                        <Item location='before' locateInMenu='auto'>
                            <DropDownButton
                                items={[
                                    { value: 2, text: t('all') },
                                    { value: 1, text: t('active') },
                                    { value: 0, text: t('passive') },
                                ]}
                                stylingMode='text'
                                text={t(settingStatus === 1 ? 'active' : settingStatus === 0 ? 'passive' : 'all')}
                                dropDownOptions={{ width: 'auto' }}
                                useSelectMode
                                onSelectionChanged={handleStatusChange}
                            />
                        </Item>

          <Item location='center' locateInMenu='auto'>
            <span ref={rowCountRef}>{rowCount} {t("records")}</span>
          </Item>
                        <Item location="after" locateInMenu="never">
                            <div className="flex flex-row gap-x-2 justify-end">
                                <Button
                                    icon="plus"
                                    type="default"
                                    stylingMode="contained"
                                    visible={true}
                                    onClick={openEmptyPopup}
                                />
                            </div>
                        </Item>
                    </Toolbar>
                    <Editing
                        mode="row"
                        allowDeleting
                        texts={{
                            deleteRow: t("delete"),
                            confirmDeleteMessage: t("confirmDeleteMessage"),
                            confirmDeleteYes: t("yes"),
                            cancelRowChanges: t("no"),
                        }}
                    />

                    <Column dataField="settingName" caption={t("themeName")} />
                    <Column dataField="comment" caption={t("comment")} alignment="left" />
                    {/* <Column dataField="settingStatus" caption="Status" alignment="left" /> */}
                    <Column
                        dataField="isDefault"
                        caption={t("default")}
                        alignment="left"
                        cellRender={(cellInfo) => (
                            <CheckBox
                                disabled
                                value={Number(cellInfo.value) === 1}
                            />
                        )}
                    />

                </DataGrid>
            </div>
            <ThemePopup
                selectedTheme={selectedTheme}
                previewVisible={popupVisible}
                onHidingPreview={() => setPopupVisible(false)}
                refresh={getList}
                onApplyTheme={(newTheme) => {
                    // Global tema güncelleme işlemi burada yapılabilir.
                    console.log("Yeni tema:", newTheme);
                    setSelectedTheme(newTheme);
                }}
            />

            <SettingsPopup
                visible={settingsPopupVisible}
                onClose={() => setSettingsPopupVisible(false)}
                onSave={handleSettingsSave}
                savedSettings={savedSettings}
                getCurrentGridColumns={() => getCurrentGridColumns(gridRef.current?.instance())}
                onSelectSetting={(setting: SavedSetting) => {
                    applySettingsToGrid(gridRef.current?.instance(), setting.settingJson);
                    setSettingsPopupVisible(true);
                }}
            />
        </div>
    );
};


